import { useState, useCallback, useEffect, useRef } from "react"
import { MessageCircle, X, Minus } from "lucide-react"
import Chat from "@/components/Chat"

interface Props {
  onActivity?: () => void
}

export default function ChatDrawer({ onActivity }: Props) {
  const [open, setOpen] = useState(false)
  const [minimized, setMinimized] = useState(false)
  const backdropRef = useRef<HTMLDivElement>(null)

  const toggle = useCallback(() => {
    if (open) {
      setOpen(false)
      setMinimized(false)
    } else {
      setOpen(true)
      setMinimized(false)
    }
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); setMinimized(false) }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [open])

  // Open via custom event (from CommandScreen quick actions)
  useEffect(() => {
    const handler = () => { setOpen(true); setMinimized(false) }
    window.addEventListener("pulse:open-chat", handler)
    return () => window.removeEventListener("pulse:open-chat", handler)
  }, [])

  return (
    <>
      {/* Floating action button — hidden on mobile where bottom-bar Chat button exists */}
      <button
        onClick={toggle}
        className={`fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full items-center justify-center shadow-lg transition-all hidden lg:flex ${
          open
            ? "bg-gray-700 hover:bg-gray-600"
            : "bg-accent hover:bg-accent-hover"
        }`}
        style={{ boxShadow: "0 4px 20px rgba(99, 102, 241, 0.3)" }}
      >
        {open ? <X size={20} className="text-white" /> : <MessageCircle size={20} className="text-white" />}
      </button>

      {/* Drawer sheet */}
      {open && (
        <>
          {/* Backdrop */}
          <div
            ref={backdropRef}
            onClick={() => { setOpen(false); setMinimized(false) }}
            className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm md:hidden"
          />

          {/* Sheet */}
          <div
            className={`fixed z-30 bg-surface border border-border rounded-t-2xl md:rounded-xl shadow-2xl flex flex-col transition-all duration-200 ${
              minimized
                ? "bottom-20 right-6 w-80 h-12"
                : "bottom-0 left-0 right-0 md:bottom-6 md:right-6 md:left-auto md:w-[420px]"
            }`}
            style={minimized ? {} : { height: "85vh", maxHeight: "calc(100vh - 100px)" }}
          >
            {/* Drag handle / header */}
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border shrink-0">
              <div className="w-8 h-1 rounded-full bg-gray-700 mx-auto md:hidden" />
              <span className="text-sm font-medium text-gray-300 hidden md:block">Pulse Chat</span>
              <div className="flex-1" />
              <button onClick={() => setMinimized(!minimized)} className="text-gray-500 hover:text-gray-300 hidden md:block">
                <Minus size={14} />
              </button>
              <button onClick={() => { setOpen(false); setMinimized(false) }} className="text-gray-500 hover:text-gray-300 hidden md:block">
                <X size={14} />
              </button>
            </div>

            {/* Chat content */}
            {!minimized && (
              <div className="flex-1 overflow-hidden">
                <Chat
                  onActivity={onActivity ? (agents) => onActivity() : undefined}
                />
              </div>
            )}
          </div>
        </>
      )}
    </>
  )
}
